import { pgTable, uuid, text, integer, timestamp, jsonb, boolean, index, uniqueIndex } from "drizzle-orm/pg-core";

/** WhatsApp session tracking — 24h window per contact */
export const whatsappSessions = pgTable("whatsapp_sessions", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  personId: uuid("person_id").notNull(),
  waId: text("wa_id").notNull(),  // WhatsApp ID (E.164 phone)
  lastInboundAt: timestamp("last_inbound_at", { withTimezone: true }),
  windowExpiresAt: timestamp("window_expires_at", { withTimezone: true }),
  isOpen: boolean("is_open").default(false).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_wa_sessions_tenant_person").on(table.tenantId, table.personId),
  index("idx_wa_sessions_wa_id").on(table.waId),
]);

/** WhatsApp message templates (Meta-approved HSMs) */
export const whatsappTemplates = pgTable("whatsapp_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  wabaId: text("waba_id").notNull(),  // WhatsApp Business Account ID
  templateName: text("template_name").notNull(),
  category: text("category").notNull(),  // MARKETING, UTILITY, AUTHENTICATION
  language: text("language").notNull(),  // en_US, pt_BR, ro, etc.
  components: jsonb("components").default([]).notNull(),  // header, body, footer, buttons
  status: text("status").default("pending").notNull(),  // pending, approved, rejected, paused, disabled
  metaTemplateId: text("meta_template_id"),
  rejectionReason: text("rejection_reason"),
  submittedAt: timestamp("submitted_at", { withTimezone: true }),
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_wa_templates_tenant").on(table.tenantId),
  index("idx_wa_templates_status").on(table.tenantId, table.status),
  uniqueIndex("uq_wa_templates_tenant_name_lang").on(table.tenantId, table.templateName, table.language),
]);

export const WHATSAPP_TEMPLATE_STATUSES = ["pending", "approved", "rejected", "paused", "disabled"] as const;
export type WhatsAppTemplateStatus = (typeof WHATSAPP_TEMPLATE_STATUSES)[number];

export const WHATSAPP_CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION"] as const;
export type WhatsAppCategory = (typeof WHATSAPP_CATEGORIES)[number];

/** Per-conversation billing for WhatsApp (different from per-message SMS) */
export const whatsappConversationBilling = pgTable("whatsapp_conversation_billing", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  personId: uuid("person_id").notNull(),
  conversationId: uuid("conversation_id"),
  category: text("category").notNull(),  // marketing, utility, authentication, service
  openedAt: timestamp("opened_at", { withTimezone: true }).notNull(),
  closedAt: timestamp("closed_at", { withTimezone: true }),
  messageCount: integer("message_count").default(0).notNull(),
  billedAmountCents: integer("billed_amount_cents").default(0).notNull(),
  country: text("country"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_wa_billing_tenant").on(table.tenantId),
  index("idx_wa_billing_tenant_date").on(table.tenantId, table.openedAt),
]);
